import { reactive, watch, onUnmounted, Ref } from 'vue';

export interface ImageBounds {
  top: number;
  left: number;
  width: number;
  height: number;
}

// Computes where the image content actually renders within the wrapper,
// accounting for object-fit: scale-down centering the content inside the
// <img> element, and the element's position within the wrapper div.
export const useImageBounds = (imageRef: Ref<HTMLImageElement | null>, enabled: Ref<boolean>) => {
  const imageBounds = reactive<ImageBounds>({ top: 0, left: 0, width: 0, height: 0 });
  let resizeObserver: ResizeObserver | null = null;

  const updateImageBounds = () => {
    const img = imageRef.value;
    const wrapper = img?.parentElement;
    if (!img || !wrapper || !img.naturalWidth || !img.naturalHeight) {
      return;
    }

    const elemWidth = img.clientWidth;
    const elemHeight = img.clientHeight;
    if (elemWidth === 0 || elemHeight === 0) {
      return;
    }

    // object-scale-down = min(contain, none): if the image fits at natural size
    // it renders at natural size; otherwise it scales down like contain.
    const fitsNaturally = img.naturalWidth <= elemWidth && img.naturalHeight <= elemHeight;

    let renderWidth: number;
    let renderHeight: number;

    if (fitsNaturally) {
      renderWidth = img.naturalWidth;
      renderHeight = img.naturalHeight;
    } else {
      const naturalRatio = img.naturalWidth / img.naturalHeight;
      const elemRatio = elemWidth / elemHeight;

      if (naturalRatio > elemRatio) {
        renderWidth = elemWidth;
        renderHeight = elemWidth / naturalRatio;
      } else {
        renderHeight = elemHeight;
        renderWidth = elemHeight * naturalRatio;
      }
    }

    // Content is centered within the element by object-fit (default object-position: 50% 50%)
    const contentOffsetTop = (elemHeight - renderHeight) / 2;
    const contentOffsetLeft = (elemWidth - renderWidth) / 2;

    // Use getBoundingClientRect to reliably compute the img element's position
    // relative to the wrapper, avoiding offsetTop/offsetLeft quirks in flex layouts.
    const wrapperRect = wrapper.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    imageBounds.top = imgRect.top - wrapperRect.top + contentOffsetTop;
    imageBounds.left = imgRect.left - wrapperRect.left + contentOffsetLeft;
    imageBounds.width = renderWidth;
    imageBounds.height = renderHeight;
  };

  // Watch both enabled and imageRef so the observer is attached even when
  // the feature flag resolves after mount (the common async case).
  watch(
    [enabled, imageRef],
    ([isEnabled, img]) => {
      resizeObserver?.disconnect();
      resizeObserver = null;

      if (isEnabled && img) {
        resizeObserver = new ResizeObserver(updateImageBounds);
        resizeObserver.observe(img);
        updateImageBounds();
      }
    },
    { immediate: true }
  );

  onUnmounted(() => {
    resizeObserver?.disconnect();
  });

  return {
    imageBounds,
    updateImageBounds,
  };
};
